import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useApp } from '../../contexts/AppContext';
import { useTenant } from '../../contexts/TenantContext';
import { assetsService } from '../../services/assets.service';
import { supabase } from '../../supabaseClient';
import { sendNotification } from '../../notifications';
import { formatDate } from '../../lib/helpers';
import { Wrench, Paperclip, QrCode, Printer, Ban, RefreshCw, ClipboardList, Plus, ArrowDownAZ, ArrowDownZA } from 'lucide-react';

const pad = (n, len=2) => String(n).padStart(len,'0');

const getMaintenanceStatus = (asset) => {
  if (!asset.next_maintenance_date) return { label: 'No Schedule', color: '#7090a0' };
  const next = new Date(asset.next_maintenance_date);
  const today = new Date();
  const days = Math.ceil((next - today) / 86400000);
  if (days < 0)   return { label: `Overdue ${Math.abs(days)}d`, color: '#e05757' };
  if (days <= 30) return { label: `Due in ${days}d`, color: '#f5a623' };
  return { label: 'OK', color: '#00e5a0' };
};
// CONDITIONS now loaded from settings (see useApp)
const ASSET_TYPES = ['Laptop','Server','Forklift','Scanner','Tablet','Projector','Printer','Network Switch','UPS','CCTV Camera','Fire Extinguisher','Power Tool','Measuring Device'];
const DEFAULT_MAINT_INTERVALS = [
  { days:30,  label:'Monthly' },
  { days:60,  label:'Every 2 Months' },
  { days:90,  label:'Quarterly' },
  { days:180, label:'Every 6 Months' },
  { days:365, label:'Yearly' },
  { days:730, label:'Every 2 Years' },
];
const STATUS_COLOR = { Available:'#00e5a0','In Use':'#5b9cf6','Under Maintenance':'#f5a623','In Transit':'#b89de8' };

function Chip({ label }) {
  const c = STATUS_COLOR[label] || '#a0b4c0';
  return <span style={{ background:c+'18', border:`1px solid ${c}30`, color:c, borderRadius:6, padding:'2px 9px', fontSize:11, fontWeight:700 }}>{label}</span>;
}
function Lbl({ children }) { return <div style={{ fontSize:10,color:'#7090a0',textTransform:'uppercase',letterSpacing:'1px',fontWeight:600,marginBottom:6 }}>{children}</div>; }
function Modal({ title, sub, onClose, children, wide, noClickOutside }) {
  return <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,.88)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:20 }}
    onClick={e=>{ if(noClickOutside) return; if(e.target===e.currentTarget) onClose(); }}>
    <div style={{ background:'#090e15',border:'1px solid #1a2a3a',borderRadius:14,width:'100%',maxWidth:wide?780:640,maxHeight:'90vh',overflow:'hidden',display:'flex',flexDirection:'column' }}
      onClick={e=>e.stopPropagation()}>
      <div style={{ padding:'18px 24px',borderBottom:'1px solid #1a2a3a',flexShrink:0,display:'flex',alignItems:'flex-start',justifyContent:'space-between' }}>
        <div>
          <div style={{ fontFamily:"'Syne',sans-serif",fontSize:16,fontWeight:800,color:'#e8edf2' }}>{title}</div>
          {sub&&<div style={{ fontSize:11,color:'#7090a0',marginTop:3,fontFamily:"'IBM Plex Mono',monospace" }}>{sub}</div>}
        </div>
        <button onClick={onClose} style={{ background:'none',border:'none',color:'#7090a0',cursor:'pointer',fontSize:20,lineHeight:1,padding:'0 0 0 16px',flexShrink:0 }}>✕</button>
      </div>
      <div style={{ padding:24,overflowY:'auto',flex:1 }}>{children}</div>
    </div>
  </div>;
}

export default function AssetsPage() {
  const { assets, setAssets, facilities, settings, perms, showToast, profile } = useApp();
  const { features } = useTenant();
  const isAdmin = profile?.role === 'Admin';
  // Respect the configurable per-org role permissions (Settings toggles), with a
  // role-based fallback if perms haven't loaded yet.
  const canEdit     = perms?.canEditAssets     ?? (profile?.role === 'Admin' || profile?.role === 'Operator');
  const canRegister = perms?.canRegisterAssets ?? (profile?.role === 'Admin' || profile?.role === 'Operator');

  const CONDITIONS = settings?.assetConditions || [
    { id:'excellent',    name:'Excellent',    color:'#00e5a0' },
    { id:'good',         name:'Good',         color:'#5b9cf6' },
    { id:'fair',         name:'Fair',         color:'#f5a623' },
    { id:'needs-repair', name:'Needs Repair', color:'#e05757' },
  ];
  const COND_COLOR = Object.fromEntries(CONDITIONS.map(c => [c.name, c.color]));
  const decommHide         = settings?.decommissionHide || 'never';
  const decommReqReason    = settings?.decommissionRequireReason || false;
  const epcRecycleEnabled  = settings?.epcRecycleEnabled || false;
  const epcRecycleAutoRelease = settings?.epcRecycleAutoRelease || false;
  const [decommModal,  setDecommModal]  = useState(null);
  const [decommReason, setDecommReason] = useState('');
  const { org } = useTenant();
  const isISO = org?.asset_profile === 'iso55000';
  const TYPES = (settings?.assetTypes||[]).map(t=>t.name).length > 0 ? (settings?.assetTypes||[]).map(t=>t.name) : ASSET_TYPES;
  const MAINT_INTERVALS = (settings?.maintenanceIntervals?.length ? settings.maintenanceIntervals : DEFAULT_MAINT_INTERVALS);
  const [modal,setModal]   = useState(null);
  const [search,setSearch] = useState('');
  const [fFilter,setFFilter] = useState('All');
  const [sFilter,setSFilter] = useState('All');
  const [sortDir,setSortDir] = useState('asc'); // 'asc' = A–Z, 'desc' = Z–A
  const [form,setForm]     = useState({});
  const [tab,setTab]       = useState('details');
  const [saving,setSaving]             = useState(false);
  const [maintHistory,setMaintHistory] = useState([]);
  const [maintLoading,setMaintLoading] = useState(false);
  const [showLogForm,setShowLogForm]   = useState(false);
  const [maintLog,setMaintLog]         = useState({ notes:'', cost:'', performed_at: new Date().toISOString().slice(0,10) });
  const [attachments,setAttachments]   = useState([]);
  const [attLoading,setAttLoading]     = useState(false);
  const [attUploading,setAttUploading] = useState(false);
  const [attCategory,setAttCategory]  = useState('Warranty');
  const [scanMode,setScanMode]   = useState(false);
  const [epcListening,setEpcListening] = useState(false); // true = waiting for tag scan
  const [epcPollTimer,setEpcPollTimer] = useState(null);  // interval ref
  const epcSeenRef = useRef(null);  // last EPC seen before listening started   // true while scanner is typing
  const [scanFlash,setScanFlash] = useState(null);  // 'found' | 'notfound'
  const searchRef = useRef(null);
  const scanBuf   = useRef('');
  const scanTimer = useRef(null);

  // ── Scanner detection ────────────────────────────────────────
  // RFID/QR scanners send keystrokes extremely fast (< 50ms apart) then Enter.
  // We detect this speed difference to distinguish human typing from scanner input.
  const handleScanInput = useCallback((raw) => {
    let term = raw.trim();
    // Try to parse JSON (QR code payload)
    if (term.startsWith('{')) {
      try {
        const obj = JSON.parse(term);
        // Extract best search key: prefer EPC, fallback inventory
        term = obj.epc || obj.inventory || obj.name || term;
      } catch(e) { /* not JSON, use raw */ }
    }
    setSearch(term);
    setScanMode(false);

    // Find match
    const q = term.toLowerCase();
    const match = assets.find(a =>
      a.epc?.toLowerCase() === q ||
      a.inventoryNumber?.toLowerCase() === q ||
      a.serial_no?.toLowerCase() === q
    );
    if (match) {
      setScanFlash('found');
      showToast('📡 Asset found: ' + match.name);
      setTimeout(() => { setForm({...match}); setTab('details'); setModal(match); }, 300);
    } else {
      setScanFlash('notfound');
      showToast('⚠ No asset matched: ' + term, 'error');
    }
    setTimeout(() => setScanFlash(null), 1500);
  }, [assets, showToast]);

  const [camOpen,setCamOpen]       = useState(false);
  const [camError,setCamError]     = useState('');
  const [camResult,setCamResult]   = useState(null); // 'found'|'notfound'
  const videoRef  = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef    = useRef(null);

  const stopCamera = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setCamOpen(false);
    setCamError('');
    setCamResult(null);
  }, []);

  const startCamera = useCallback(async () => {
    setCamError('');
    setCamResult(null);
    setCamOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      streamRef.current = stream;
      // Wait for modal to mount
      await new Promise(r => setTimeout(r, 80));
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        scanFrame();
      }
    } catch(err) {
      setCamError(err.name === 'NotAllowedError' ? 'Camera permission denied. Please allow camera access in your browser.' : 'Could not open camera: ' + err.message);
    }
  }, []);

  const scanFrame = useCallback(() => {
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState < 2) { rafRef.current = requestAnimationFrame(scanFrame); return; }
    const ctx = canvas.getContext('2d');
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);
    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);

    // Use window.jsQR if loaded, else try BarcodeDetector
    if (window.jsQR) {
      const code = window.jsQR(imgData.data, imgData.width, imgData.height, { inversionAttempts: 'dontInvert' });
      if (code?.data) { handleCamScan(code.data); return; }
    } else if (window._barcodeDetector) {
      window._barcodeDetector.detect(canvas).then(codes => {
        if (codes.length) handleCamScan(codes[0].rawValue);
      });
    }
    rafRef.current = requestAnimationFrame(scanFrame);
  }, []);

  const handleCamScan = useCallback((raw) => {
    cancelAnimationFrame(rafRef.current);
    let term = raw.trim();
    if (term.startsWith('{')) {
      try { const obj = JSON.parse(term); term = obj.epc || obj.inventory || obj.name || term; } catch(e){}
    }
    setSearch(term);
    const q = term.toLowerCase();
    const match = assets.find(a =>
      a.epc?.toLowerCase() === q ||
      a.inventoryNumber?.toLowerCase() === q ||
      a.serial_no?.toLowerCase() === q ||
      a.name?.toLowerCase() === q
    );
    if (match) {
      setCamResult('found');
      setTimeout(() => { stopCamera(); setForm({...match}); setTab('details'); setModal(match); showToast('📷 Found: ' + match.name); }, 900);
    } else {
      setCamResult('notfound');
      setTimeout(() => { setCamResult(null); rafRef.current = requestAnimationFrame(scanFrame); }, 1200);
    }
  }, [assets, stopCamera, showToast]);

  // Load jsQR dynamically once
  useEffect(() => {
    if (!window.jsQR && !window._jsqrLoading) {
      window._jsqrLoading = true;
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.js';
      s.onload = () => { window._jsqrLoading = false; };
      document.head.appendChild(s);
    }
    // Fallback: BarcodeDetector API
    if ('BarcodeDetector' in window && !window._barcodeDetector) {
      window._barcodeDetector = new window.BarcodeDetector({ formats: ['qr_code'] });
    }
    return () => { stopCamera(); };
  }, [stopCamera]);

  const lastKeyTime = useRef(0);
  const handleSearchKeyDown = useCallback((e) => {
    const now = Date.now();
    const gap  = now - lastKeyTime.current;
    lastKeyTime.current = now;

    // Scanner fires Enter after the barcode — trigger scan logic
    if (e.key === 'Enter') {
      const val = e.target.value.trim();
      if (val) { handleScanInput(val); }
      e.preventDefault();
      return;
    }

    // If keystrokes are coming in faster than 50ms, flag as scanner mode
    if (gap < 50) setScanMode(true);
    else setScanMode(false);

    // Clear any pending scan timer
    clearTimeout(scanTimer.current);
    // Auto-trigger if no new key within 200ms (end of scan stream without Enter)
    scanTimer.current = setTimeout(() => {
      const val = searchRef.current?.value.trim() || '';
      if (scanMode && val.length > 4) handleScanInput(val);
      setScanMode(false);
    }, 200);
  }, [handleScanInput, scanMode]);

  // Filter out auto-hidden decommissioned assets
  const visibleAssets = useMemo(() => {
    if (decommHide === 'never') return assets;
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - parseInt(decommHide));
    return assets.filter(a => {
      if (a.status !== 'Decommissioned') return true;
      if (!a.decommissioned_at) return false;
      return new Date(a.decommissioned_at) > cutoff;
    });
  }, [assets, decommHide]);

  const filtered = useMemo(()=>{
    let d = visibleAssets;
    if(search){ const q=search.toLowerCase(); d=d.filter(a=>a.name?.toLowerCase().includes(q)||a.inventoryNumber?.toLowerCase().includes(q)||a.epc?.toLowerCase().includes(q)||a.serial_no?.toLowerCase().includes(q)); }
    if(fFilter!=='All') d=d.filter(a=>a.currentFacility===fFilter || a.current_facility===fFilter || a.facility_id===fFilter);
    if(sFilter!=='All') d=d.filter(a=>a.status===sFilter);
    const dir = sortDir==='desc' ? -1 : 1;
    return [...d].sort((a,b)=>dir*(a.name||'').localeCompare(b.name||'','en',{ sensitivity:'base',numeric:true }));
  },[visibleAssets,search,fFilter,sFilter,sortDir]);

  // Poll the bridge for a new tag read and auto-fill EPC field
  const startEpcListen = () => {
    setEpcListening(true);
    epcSeenRef.current = null;
    const bridgeUrl = localStorage.getItem('rfid_bridge_url') || 'http://localhost:3500';

    // Snapshot the most recent EPC so we only detect NEW scans
    fetch(`${bridgeUrl}/api/tag-history?limit=1`)
      .then(r => r.json())
      .then(data => {
        epcSeenRef.current = Array.isArray(data) ? data[0]?.epc || null : null;
      })
      .catch(() => {
        showToast('❌ Bridge not reachable — make sure bridge is running');
        setEpcListening(false);
      });

    // Poll /api/tag-history every 500ms for a new tag
    const timer = setInterval(() => {
      fetch(`${bridgeUrl}/api/tag-history?limit=1`)
        .then(r => r.json())
        .then(data => {
          const newest = Array.isArray(data) ? data[0]?.epc : null;
          if (newest && newest !== epcSeenRef.current) {
            setForm(f => ({ ...f, epc: newest }));
            stopEpcListen(timer);
            showToast(`✓ EPC captured: ${newest.slice(0,16)}…`);
          }
        })
        .catch(() => {});
    }, 500);
    setEpcPollTimer(timer);

    // Auto-stop after 30 seconds
    setTimeout(() => stopEpcListen(timer), 30000);
  };

  const stopEpcListen = (timer) => {
    clearInterval(timer || epcPollTimer);
    setEpcListening(false);
    setEpcPollTimer(null);
  };

  // Collect EPCs from decommissioned assets that have been released for recycling
  const recycledEpcs = useMemo(() =>
    assets.filter(a => a.status === 'Decommissioned' && a.epc_released && a.released_epc)
          .map(a => ({ epc: a.released_epc, fromAsset: a.name, assetId: a.id })),
    [assets]
  );

  const releaseEpc = async (asset) => {
    // Store EPC in released_epc, then clear epc column immediately — no duplicate risk
    const { error } = await supabase.from('assets')
      .update({ epc_released: true, released_epc: asset.epc, epc: null })
      .eq('id', asset.id);
    if (!error) {
      setAssets(prev => prev.map(a => a.id === asset.id
        ? { ...a, epc_released: true, released_epc: asset.epc, epc: null } : a));
      showToast(`♻ EPC ${asset.epc} released for recycling`);
    } else {
      showToast('❌ ' + error.message);
    }
  };

  const reclaimEpc = async (epc, assetId) => {
    // Mark old asset's EPC as consumed and clear it
    await supabase.from('assets').update({ epc: null, epc_released: false }).eq('id', assetId);
    setAssets(prev => prev.map(a => a.id === assetId ? { ...a, epc: null, epc_released: false } : a));
  };

  const decommissionAsset = async (asset) => {
    if (decommReqReason && !decommReason.trim()) {
      showToast('❌ Please enter a reason'); return;
    }
    try {
      const { supabase } = await import('../../supabaseClient');
      await supabase.from('assets').update({
        status: 'Decommissioned',
        decommissioned_at: new Date().toISOString(),
        decommission_reason: decommReason || null,
        epc_released: epcRecycleAutoRelease ? true : false,
        released_epc: epcRecycleAutoRelease ? decommModal.epc : null,
        epc: epcRecycleAutoRelease ? null : decommModal.epc,
      }).eq('id', asset.id);
      setAssets(prev => prev.map(a => a.id === asset.id
        ? { ...a, status:'Decommissioned', decommissioned_at:new Date().toISOString(), decommission_reason:decommReason }
        : a));
      showToast(`✓ "${asset.name}" decommissioned`);
      setDecommModal(null); setDecommReason(''); setModal(null);
    } catch(err) { showToast('❌ ' + err.message); }
  };

  const loadMaintHistory = async (assetId) => {
    setMaintLoading(true);
    const { data } = await supabase.from('maintenance_history')
      .select('*').eq('asset_id', assetId).order('created_at', { ascending: false });
    setMaintHistory(data || []);
    setMaintLoading(false);
  };

  const logMaintenance = async () => {
    if (!modal?.id) return;
    setSaving(true);
    try {
      const entry = {
        org_id:       profile?.org_id,
        asset_id:     modal.id,
        asset_name:   modal.name,
        performed_by: profile?.name || profile?.email,
        performed_at: maintLog.performed_at,
        notes:        maintLog.notes,
        cost:         maintLog.cost ? Number(maintLog.cost) : null,
        next_date:    form.next_maintenance_date || null,
      };
      const { error } = await supabase.from('maintenance_history').insert([entry]);
      if (error) throw new Error(error.message);

      // Update asset maintenance dates
      await assetsService.update(modal.id, {
        maintenance_date:      maintLog.performed_at,
        maintenance_status:    'OK',
      });

      setMaintHistory(prev => [{ ...entry, id: Date.now().toString(), created_at: new Date().toISOString() }, ...prev]);
      setMaintLog({ notes: '', cost: '', performed_at: new Date().toISOString().slice(0,10) });
      setShowLogForm(false);
      showToast('✅ Maintenance logged');
    } catch(e) { showToast('❌ ' + e.message); }
    setSaving(false);
  };

  const ATT_CATS = ['Warranty','Manual','Receipt','Service Report','Photo','Other'];

  const fileIcon = (type) => {
    if (!type) return '📎';
    if (type.includes('pdf')) return '📄';
    if (type.includes('image')) return '🖼';
    if (type.includes('word') || type.includes('doc')) return '📝';
    if (type.includes('sheet') || type.includes('excel') || type.includes('csv')) return '📊';
    if (type.includes('zip') || type.includes('rar')) return '🗜';
    return '📎';
  };

  const fmtSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1048576) return `${(bytes/1024).toFixed(1)}KB`;
    return `${(bytes/1048576).toFixed(1)}MB`;
  };

  const loadAttachments = async (assetId) => {
    setAttLoading(true);
    const { data } = await supabase.from('asset_attachments')
      .select('*').eq('asset_id', assetId).order('created_at', { ascending: false });
    setAttachments(data || []);
    setAttLoading(false);
  };

  const uploadAttachment = async (file, assetId) => {
    if (!file || !assetId) return;
    setAttUploading(true);
    try {
      const ext      = file.name.split('.').pop();
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_'); // remove spaces & special chars
      const path     = `${profile?.org_id}/${assetId}/${Date.now()}_${safeName}`;
      const { error: upErr } = await supabase.storage.from('attachments').upload(path, file, { upsert: false });
      if (upErr) throw new Error(upErr.message);

      const { data: urlData } = supabase.storage.from('attachments').getPublicUrl(path);
      const fileUrl = urlData?.publicUrl || '';

      const { data: row, error: dbErr } = await supabase.from('asset_attachments').insert([{
        org_id:      profile?.org_id,
        asset_id:    assetId,
        asset_name:  modal?.name,
        file_name:   file.name,
        file_url:    fileUrl,
        file_path:   path,
        file_type:   file.type,
        file_size:   file.size,
        category:    attCategory,
        uploaded_by: profile?.name || profile?.email,
      }]).select().single();
      if (dbErr) throw new Error(dbErr.message);

      setAttachments(prev => [row, ...prev]);
      showToast(`✅ ${file.name} uploaded`);
    } catch(e) { showToast('❌ ' + e.message); }
    setAttUploading(false);
  };

  const deleteAttachment = async (att) => {
    if (!window.confirm(`Delete "${att.file_name}"?`)) return;
    try {
      await supabase.storage.from('attachments').remove([att.file_path]);
      await supabase.from('asset_attachments').delete().eq('id', att.id);
      setAttachments(prev => prev.filter(a => a.id !== att.id));
      showToast('🗑 Attachment deleted');
    } catch(e) { showToast('❌ ' + e.message); }
  };

  const downloadAttachment = async (att) => {
    const { data, error } = await supabase.storage.from('attachments').download(att.file_path);
    if (error) { showToast('❌ Download failed'); return; }
    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url; a.download = att.file_name; a.click();
    URL.revokeObjectURL(url);
  };

  const saveAdd = async () => {
    if(!form.name||!form.facility_id) return;
    setSaving(true);
    try {
      const fac = facilities.find(f=>f.id===form.facility_id);
      const timestamp = Date.now();
      const inventoryNumber = `INV-${new Date().getFullYear()}-${String(timestamp).slice(-6)}`;

      // ── If recycling EPC, mark old asset as consumed ──
      if (form.recycleEpcId) {
        // EPC was already cleared from old asset when it was released — just mark as consumed
        const { error: consumeErr } = await supabase
          .from('assets')
          .update({ epc_released: false, released_epc: null })
          .eq('id', form.recycleEpcId);
        if (consumeErr) throw new Error('Failed to consume recycled EPC: ' + consumeErr.message);
        setAssets(prev => prev.map(a =>
          a.id === form.recycleEpcId ? { ...a, epc_released: false, released_epc: null } : a
        ));
      }

      const newAsset = await assetsService.create({
        ...form,
        epc: form.epc || '',
        inventory_number: inventoryNumber,
        home_facility_id: form.facility_id,
        home_facility_name: fac?.name || '',
        current_facility_name: fac?.name || '',
        status: 'Available',
        org_id: profile?.org_id,
      });
      setAssets(prev => [newAsset, ...prev]);
      showToast('✓ Asset registered' + (form.recycleEpcId ? ' with recycled EPC ♻' : ''));
      setModal(null);
    } catch(err){ showToast('❌ ' + err.message, 'error'); }
    setSaving(false);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const updated = await assetsService.update(modal.id, form);
      // Notify if condition changed to Needs Repair
      if (form.condition === 'Needs Repair' && modal.condition !== 'Needs Repair') {
        sendNotification('maintenance', {
          assetName: modal.name,
          epc:       modal.epc,
          facility:  modal.currentFacilityName,
          byUser:    profile?.name || profile?.email,
        }, settings);
      }
      setAssets(prev=>prev.map(a=>a.id===modal.id?{...a,...updated}:a));
      showToast('✓ Asset updated');
      setModal(null);
    } catch(err){ showToast('❌ '+err.message,'error'); }
    setSaving(false);
  };

  const deleteAsset = async (asset) => {
    if (!window.confirm(`Permanently delete "${asset.name}"? This cannot be undone.`)) return;
    try {
      await assetsService.delete(asset.id);
      setAssets(prev => prev.filter(a => a.id !== asset.id));
      showToast(`🗑 "${asset.name}" permanently deleted`);
      setModal(null);
    } catch(err) { showToast('❌ ' + err.message, 'error'); }
  };


  const printLabel = (asset) => {
    const companyName = org?.name || '';
    const qrData = JSON.stringify({
      epc: asset.epc||'',
      name: asset.name,
      inventory: asset.inventoryNumber||'',
      status: asset.status||'',
      facility: asset.currentFacilityName||'',
      company: companyName,
      serial: asset.serial_no||'',
    });
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrData)}&format=png&color=000000&bgcolor=ffffff&margin=8`;
    const statusColor = {Available:'#00a86b','In Use':'#2563eb','Under Maintenance':'#d97706','In Transit':'#7c3aed'}[asset.status]||'#555';
    const win = window.open('','_blank','width=520,height=680');
    win.document.write(`<!DOCTYPE html>
<html>
<head>
<title>Asset Label - ${asset.name}</title>
<style>
  @page { size: 100mm 65mm; margin: 0; }
  @media print { body { margin: 0; } .no-print { display: none; } }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; background: #f0f0f0; display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; padding: 8px; }
  .label { width: 96mm; background: #fff; border: 1.5px solid #222; border-radius: 4mm; overflow: hidden; box-shadow: 0 2px 12px rgba(0,0,0,.15); }
  .header { background: #111; color: #fff; padding: 3mm 4mm; display: flex; align-items: center; justify-content: space-between; }
  .company { font-size: 10px; font-weight: 700; letter-spacing: .5px; text-transform: uppercase; color: #fff; }
  .inv-no { font-family: monospace; font-size: 9px; color: #aaa; }
  .body { display: flex; gap: 3mm; padding: 3.5mm 4mm; }
  .info { flex: 1; min-width: 0; }
  .asset-name { font-size: 12px; font-weight: 700; color: #111; margin-bottom: 2.5mm; line-height: 1.25; word-break: break-word; }
  .row { display: flex; gap: 1.5mm; margin-bottom: 1.5mm; align-items: baseline; }
  .key { font-size: 6.5px; text-transform: uppercase; color: #999; width: 13mm; flex-shrink: 0; letter-spacing: .3px; }
  .val { font-size: 8px; color: #222; font-family: monospace; word-break: break-all; }
  .status-badge { display: inline-block; font-size: 7px; font-weight: 700; text-transform: uppercase; padding: 0.8mm 2mm; border-radius: 2mm; color: #fff; background: ${statusColor}; letter-spacing: .5px; }
  .qr-wrap { flex-shrink: 0; display: flex; flex-direction: column; align-items: center; gap: 1.5mm; }
  .qr-wrap img { width: 24mm; height: 24mm; display: block; border: 1px solid #eee; }
  .qr-label { font-size: 6px; color: #aaa; text-align: center; }
  .epc-bar { background: #111; color: #00e5a0; font-family: monospace; font-size: 7px; letter-spacing: .8px; text-align: center; padding: 1.8mm 3mm; word-break: break-all; }
  .print-btn { margin-top: 12px; padding: 10px 28px; background: #111; color: #fff; border: none; border-radius: 8px; font-size: 14px; cursor: pointer; font-family: Arial; }
  .print-btn:hover { background: #333; }
</style>
</head>
<body>
  <div class="label">
    <div class="header">
      <div class="company">${companyName||'—'}</div>
      <div class="inv-no">${asset.inventoryNumber||'—'}</div>
    </div>
    <div class="body">
      <div class="info">
        <div class="asset-name">${asset.name}</div>
        <div class="row"><span class="key">Type</span><span class="val">${asset.type||'—'}</span></div>
        <div class="row"><span class="key">Brand</span><span class="val">${(asset.brand||'—')+' '+(asset.model||'')}</span></div>
        <div class="row"><span class="key">Serial</span><span class="val">${asset.serial_no||'—'}</span></div>
        <div class="row"><span class="key">Facility</span><span class="val">${asset.currentFacilityName||'—'}</span></div>
        <div class="row"><span class="key">Status</span><span class="val"><span class="status-badge">${asset.status||'—'}</span></span></div>
      </div>
      <div class="qr-wrap">
        <img src="${qrUrl}" alt="QR"/>
        <div class="qr-label">SCAN TO IDENTIFY</div>
      </div>
    </div>
    <div class="epc-bar">${asset.epc||'NO EPC ASSIGNED'}</div>
  </div>
  <button class="print-btn no-print" onclick="window.print();window.close()">🖨 Print Label</button>
</body>
</html>`);
    win.document.close();
  };

  return <div>
    <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:24 }}>
      <div>
        <div style={{ fontFamily:"'Syne',sans-serif",fontSize:22,fontWeight:800,color:'#e8edf2' }}>Asset Registry</div>
        <div style={{ fontSize:12,color:'#7090a0',marginTop:4 }}>{assets.length} registered assets</div>
      </div>
      {canRegister && <button className="btn" style={{ background:'#00e5a0',color:'#060b10',display:'inline-flex',alignItems:'center',gap:6 }} onClick={()=>{setForm({name:'',type:TYPES[0]||'Laptop',brand:'',model:'',serial_no:'',condition:'Good',facility_id:facilities[0]?.id||'',notes:'',value:'',status:'Available'});setModal('add');}}><Plus size={14}/>Register Asset</button>}
    </div>

    <div style={{ display:'flex',gap:10,marginBottom:18,flexWrap:'wrap',position:'relative' }}>
      <div style={{ position:'relative',flex:'1 1 240px' }}>
        <span style={{ position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',fontSize:15,
          transition:'all .2s',
          color: scanFlash==='found'?'#00e5a0': scanFlash==='notfound'?'#e05757': scanMode?'#f5a623':'#7090a0'
        }}>{scanMode?'📡': scanFlash==='found'?'✓': scanFlash==='notfound'?'✗':'🔍'}</span>
        <input
          ref={searchRef}
          className="inp"
          style={{
            paddingLeft:34, paddingRight:search?32:12,
            transition:'border-color .2s, box-shadow .2s',
            borderColor: scanFlash==='found'?'#00e5a0': scanFlash==='notfound'?'#e05757': scanMode?'rgba(245,166,35,.6)':'',
            boxShadow: scanMode?'0 0 0 2px rgba(245,166,35,.15)': scanFlash==='found'?'0 0 0 2px rgba(0,229,160,.15)': scanFlash==='notfound'?'0 0 0 2px rgba(224,87,87,.15)':'',
          }}
          placeholder="Search or scan RFID tag / QR code…"
          value={search}
          onChange={e=>setSearch(e.target.value)}
          onKeyDown={handleSearchKeyDown}
        />
        {search && <button onClick={()=>setSearch('')} style={{ position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',color:'#7090a0',cursor:'pointer',fontSize:16,padding:'0 4px',lineHeight:1 }}>×</button>}
      </div>
      {scanMode && <div style={{ position:'absolute',top:'100%',left:0,marginTop:6,background:'rgba(245,166,35,.12)',border:'1px solid rgba(245,166,35,.3)',borderRadius:8,padding:'6px 12px',fontSize:11,color:'#f5a623',display:'flex',alignItems:'center',gap:6,zIndex:10,whiteSpace:'nowrap' }}>
        <span style={{ animation:'pulse 0.5s infinite' }}>📡</span> Scanning…
      </div>}
      <button
        title="Scan QR code with camera"
        onClick={startCamera}
        style={{ padding:'8px 14px',borderRadius:8,border:'1px solid #1a2a3a',background:'#0d1520',color:'#a0b4c0',cursor:'pointer',fontSize:18,display:'flex',alignItems:'center',gap:6,transition:'all .15s',flexShrink:0 }}
        onMouseEnter={e=>{e.currentTarget.style.borderColor='rgba(0,229,160,.4)';e.currentTarget.style.color='#00e5a0';}}
        onMouseLeave={e=>{e.currentTarget.style.borderColor='#1a2a3a';e.currentTarget.style.color='#a0b4c0';}}
      >
        <span>📷</span>
        <span style={{ fontSize:12,fontWeight:600 }}>Scan QR</span>
      </button>
      <select className="sel" value={fFilter} onChange={e=>setFFilter(e.target.value)}>
        <option value="All">All Facilities</option>
        {facilities.map(f=><option key={f.id} value={f.id}>{f.name}</option>)}
      </select>
      <select className="sel" value={sFilter} onChange={e=>setSFilter(e.target.value)}>
        <option value="All">All Statuses</option>
        {['Available','In Use','Under Maintenance','In Transit'].map(s=><option key={s}>{s}</option>)}
      </select>
      <button
        title={sortDir==='asc' ? 'Sorted A → Z (click for Z → A)' : 'Sorted Z → A (click for A → Z)'}
        onClick={()=>setSortDir(d=>d==='asc'?'desc':'asc')}
        style={{ padding:'8px 12px',borderRadius:8,border:'1px solid #1a2a3a',background:'#0d1520',color:'#a0b4c0',cursor:'pointer',display:'flex',alignItems:'center',gap:6,transition:'all .15s',flexShrink:0 }}
        onMouseEnter={e=>{e.currentTarget.style.borderColor='rgba(0,229,160,.4)';e.currentTarget.style.color='#00e5a0';}}
        onMouseLeave={e=>{e.currentTarget.style.borderColor='#1a2a3a';e.currentTarget.style.color='#a0b4c0';}}
      >
        {sortDir==='asc' ? <ArrowDownAZ size={15}/> : <ArrowDownZA size={15}/>}
        <span style={{ fontSize:12,fontWeight:600 }}>{sortDir==='asc' ? 'A–Z' : 'Z–A'}</span>
      </button>
    </div>

    {/* Table */}
    <div style={{ background:'#0d1520',border:'1px solid #1a2a3a',borderRadius:12,overflow:'hidden' }}>
      <table style={{ width:'100%',borderCollapse:'collapse',tableLayout:'fixed' }}>
        <thead><tr style={{ borderBottom:'1px solid #1a2a3a' }}>
          {(isISO
            ? [['Equipment Name',180],['Platform',90],['Location',120],['PHE Type',100],['Tag No',120],['Year Built',90],['Last Service',110],['Next Service',110]]
            : [['Asset Name',180],['Facility',140],['Type',90],['Qty',80],['Condition',90],['Status',100],['Last Maintenance',130],['Last Seen',120]]
          ).map(([h,w])=>(
            <th key={h} className="th" style={{ width:w }}>{h}</th>
          ))}
        </tr></thead>
        <tbody>
          {filtered.length===0 && <tr><td colSpan={8} style={{ textAlign:'center',padding:'40px 0',color:'#7090a0' }}>No assets found</td></tr>}
          {filtered.map(a=>(
            <tr key={a.id} className="row" onClick={()=>{setForm({...a});setTab('details');setModal(a);}}>
              <td className="td"><span style={{ fontWeight:600,color:'#e8edf2' }}>{a.name}</span>
                {a.next_maintenance_date && (() => { const ms = getMaintenanceStatus(a); return <span style={{ marginLeft:6,fontSize:10,background:ms.color+'20',color:ms.color,borderRadius:4,padding:'1px 5px' }}>{ms.label}</span>; })()}
              </td>
              {isISO ? (<>
                <td className="td"><span style={{ fontSize:12,color:'#c8d6e0' }}>{a.platform||'—'}</span></td>
                <td className="td"><span style={{ fontSize:12,color:'#a0b4c0' }}>{a.currentFacilityName||'—'}</span></td>
                <td className="td"><span style={{ fontSize:12,color:'#c8d6e0' }}>{a.phe_type||'—'}</span></td>
                <td className="td"><span style={{ fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:'#c8d6e0' }}>{a.tag_no||'—'}</span></td>
                <td className="td"><span style={{ fontSize:12,color:'#a0b4c0' }}>{a.year_built||'—'}</span></td>
                <td className="td"><span style={{ fontSize:12,color:'#a0b4c0' }}>{a.maintenance_date ? formatDate(a.maintenance_date) : '—'}</span></td>
                <td className="td"><span style={{ fontSize:12,color:'#a0b4c0' }}>{a.next_maintenance_date ? formatDate(a.next_maintenance_date) : '—'}</span></td>
              </>) : (<>
                <td className="td"><span style={{ fontSize:12,color:'#a0b4c0' }}>{a.currentFacilityName||'—'}</span></td>
                <td className="td"><span style={{ fontSize:12,color:'#c8d6e0' }}>{a.type||'—'}</span></td>
                <td className="td"><span style={{ fontSize:12,color:'#c8d6e0' }}>{a.quantity!=null ? `${a.quantity} ${a.quantity_unit||'pcs'}` : '—'}</span></td>
                <td className="td"><span style={{ fontSize:12,fontWeight:600,color:COND_COLOR[a.condition]||'#a0b4c0' }}>{a.condition||'—'}</span></td>
                <td className="td"><Chip label={a.status}/></td>
                <td className="td"><span style={{ fontSize:12,color:'#a0b4c0' }}>{a.maintenance_date ? formatDate(a.maintenance_date) : '—'}</span></td>
                <td className="td"><span style={{ fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:'#7090a0' }}>{a.lastSeen||'—'}</span></td>
              </>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {/* Camera QR Scanner Modal */}
    {camOpen && <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,.96)',zIndex:500,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:0 }}>
      {/* Header */}
      <div style={{ width:'100%',maxWidth:500,display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 20px' }}>
        <div>
          <div style={{ fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:16,color:'#e8edf2' }}>📷 Scan QR Code</div>
          <div style={{ fontSize:11,color:'#7090a0',marginTop:2 }}>Point camera at the asset QR sticker</div>
        </div>
        <button onClick={stopCamera} style={{ background:'#1a2a3a',border:'none',color:'#c8d6e0',borderRadius:8,padding:'7px 14px',cursor:'pointer',fontSize:13,fontWeight:600 }}>✕ Close</button>
      </div>

      {/* Viewfinder */}
      <div style={{ position:'relative',width:'100%',maxWidth:500,background:'#000',borderRadius:0,overflow:'hidden' }}>
        <video ref={videoRef} style={{ width:'100%',display:'block',maxHeight:'60vh',objectFit:'cover' }} playsInline muted />
        <canvas ref={canvasRef} style={{ display:'none' }} />

        {/* Finder frame overlay */}
        {!camError && !camResult && <div style={{ position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',pointerEvents:'none' }}>
          <div style={{ position:'relative',width:220,height:220 }}>
            {/* Corner brackets */}
            {[['0%','0%','right','bottom'],['0%','100%','right','top'],['100%','0%','left','bottom'],['100%','100%','left','top']].map(([t,l,br,bb],i)=>(
              <div key={i} style={{ position:'absolute',top:t,left:l,width:28,height:28,
                borderTop: bb==='bottom'?'none':`3px solid #00e5a0`,
                borderBottom: bb==='top'?'none':`3px solid #00e5a0`,
                borderLeft: br==='right'?'none':`3px solid #00e5a0`,
                borderRight: br==='left'?'none':`3px solid #00e5a0`,
                transform:`translate(${l==='100%'?'-100%':'0'},${t==='100%'?'-100%':'0'})`
              }}/>
            ))}
            {/* Scan line */}
            <div style={{ position:'absolute',left:4,right:4,height:2,background:'linear-gradient(90deg,transparent,#00e5a0,transparent)',top:'50%',animation:'scanline 2s ease-in-out infinite' }}/>
          </div>
          <style>{`@keyframes scanline{0%,100%{top:10%}50%{top:90%}}`}</style>
        </div>}

        {/* Found overlay */}
        {camResult==='found' && <div style={{ position:'absolute',inset:0,background:'rgba(0,229,160,.15)',display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:12 }}>
          <div style={{ fontSize:56 }}>✅</div>
          <div style={{ color:'#00e5a0',fontWeight:800,fontSize:18,fontFamily:"'Syne',sans-serif" }}>Asset Found!</div>
        </div>}

        {/* Not found overlay */}
        {camResult==='notfound' && <div style={{ position:'absolute',inset:0,background:'rgba(224,87,87,.12)',display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:10 }}>
          <div style={{ fontSize:48 }}>❌</div>
          <div style={{ color:'#e05757',fontWeight:700,fontSize:15 }}>No matching asset found</div>
          <div style={{ color:'#a0b4c0',fontSize:12 }}>Scanning again…</div>
        </div>}

        {/* Error state */}
        {camError && <div style={{ position:'absolute',inset:0,background:'#060b10',display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:14,padding:24,textAlign:'center' }}>
          <div style={{ fontSize:42 }}>🚫</div>
          <div style={{ color:'#e05757',fontSize:14,fontWeight:600 }}>{camError}</div>
          <button onClick={startCamera} style={{ background:'#00e5a0',color:'#060b10',border:'none',borderRadius:8,padding:'8px 20px',fontWeight:700,cursor:'pointer',fontSize:13 }}>Try Again</button>
        </div>}
      </div>

      {/* Footer hint */}
      <div style={{ width:'100%',maxWidth:500,padding:'14px 20px',display:'flex',alignItems:'center',gap:10 }}>
        <div style={{ flex:1,fontSize:12,color:'#7090a0',lineHeight:1.6 }}>
          Works with QR stickers printed from the <b style={{ color:'#a0b4c0' }}>🏷 QR Label</b> tab.<br/>
          Or type/paste EPC manually in the search box.
        </div>
      </div>
    </div>}

    {/* Add Modal */}
    {modal==='add' && <Modal title="Register New Asset" onClose={()=>setModal(null)} noClickOutside>
      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:14 }}>
        <div style={{ gridColumn:'1/-1' }}><Lbl>{isISO ? 'Equipment Name *' : 'Asset Name *'}</Lbl><input className="inp" value={form.name||''} placeholder={isISO ? 'e.g. Chilled Water/Sea Water Exchanger' : 'e.g. Dell Laptop XPS15'} onChange={e=>setForm(f=>({...f,name:e.target.value}))} /></div>
        {!isISO && <div><Lbl>Asset Type</Lbl><select className="sel" style={{ width:'100%' }} value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value}))}>{TYPES.map(t=><option key={t}>{t}</option>)}</select></div>}
        <div><Lbl>Brand</Lbl><input className="inp" value={form.brand||''} onChange={e=>setForm(f=>({...f,brand:e.target.value}))} /></div>
        <div><Lbl>Model</Lbl><input className="inp" value={form.model||''} onChange={e=>setForm(f=>({...f,model:e.target.value}))} /></div>
        <div><Lbl>Serial Number</Lbl><input className="inp" value={form.serial_no||''} onChange={e=>setForm(f=>({...f,serial_no:e.target.value}))} /></div>
        {!isISO && <div><Lbl>Condition</Lbl><select className="sel" style={{ width:'100%' }} value={form.condition} onChange={e=>setForm(f=>({...f,condition:e.target.value}))}>{CONDITIONS.map(c=><option key={c.id||c} value={c.name||c}>{c.name||c}</option>)}</select></div>}
        {!isISO && <div><Lbl>Value (MYR)</Lbl><input className="inp" type="number" value={form.value||''} onChange={e=>setForm(f=>({...f,value:e.target.value}))} /></div>}
        {isISO && <>
          <div><Lbl>Platform</Lbl><input className="inp" value={form.platform||''} placeholder="e.g. CKP" onChange={e=>setForm(f=>({...f,platform:e.target.value}))}/></div>
          <div><Lbl>PHE Type</Lbl><input className="inp" value={form.phe_type||''} placeholder="e.g. Gasketed" onChange={e=>setForm(f=>({...f,phe_type:e.target.value}))}/></div>
          <div><Lbl>Tag No</Lbl><input className="inp" value={form.tag_no||''} placeholder="e.g. CKP-E-4438A" onChange={e=>setForm(f=>({...f,tag_no:e.target.value}))}/></div>
          <div><Lbl>Year Built</Lbl><input className="inp" type="number" min="1900" max="2100" value={form.year_built||''} placeholder="e.g. 2001" onChange={e=>setForm(f=>({...f,year_built:e.target.value}))}/></div>
          <div style={{ gridColumn:'1/-1' }}><Lbl>Preventive Maintenance</Lbl><input className="inp" value={form.preventive_maintenance||''} placeholder="e.g. 5 Years after last service date" onChange={e=>setForm(f=>({...f,preventive_maintenance:e.target.value}))}/></div>
        </>}
        <div style={{ gridColumn:'1/-1' }}><Lbl>Assign to Facility *</Lbl><select className="sel" style={{ width:'100%' }} value={form.facility_id} onChange={e=>setForm(f=>({...f,facility_id:e.target.value}))}>{facilities.map(f=><option key={f.id} value={f.id}>{f.name}</option>)}</select></div>
        <div style={{ gridColumn:'1/-1' }}>
          <Lbl>EPC Tag Code <span style={{ color:'#7090a0',textTransform:'none',letterSpacing:0,fontWeight:400 }}>(type or auto-capture)</span></Lbl>
          <div style={{ position:'relative' }}>
            <span style={{ position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',fontSize:16 }}>
              {epcListening ? '🔴' : '📡'}
            </span>
            <input className="inp"
              style={{ paddingLeft:38,paddingRight:90,fontFamily:"'IBM Plex Mono',monospace",fontSize:13,letterSpacing:'1px',
                borderColor: epcListening ? '#e05757' : '',
                boxShadow: epcListening ? '0 0 0 2px rgba(224,87,87,.15)' : '' }}
              value={form.epc||''}
              placeholder={epcListening ? 'Hold tag near antenna…' : 'E28011606000020000000001'}
              onChange={e=>setForm(f=>({...f,epc:e.target.value.toUpperCase()}))}
              readOnly={epcListening}
            />
            <button type="button"
              style={{ position:'absolute',right:6,top:'50%',transform:'translateY(-50%)',
                background: epcListening ? '#e05757' : '#00e5a018',
                border: `1px solid ${epcListening ? '#e05757' : '#00e5a030'}`,
                color: epcListening ? '#fff' : '#00e5a0',
                borderRadius:6,padding:'4px 10px',fontSize:11,fontWeight:700,cursor:'pointer' }}
              onClick={()=> epcListening ? stopEpcListen() : startEpcListen()}>
              {epcListening ? '■ Stop' : '▶ Scan'}
            </button>
          </div>
          <div style={{ fontSize:11,color: epcListening ? '#e05757' : '#7090a0',marginTop:5 }}>
            {epcListening
              ? '🔴 Listening… hold your RFID tag near the antenna now'
              : 'Click ▶ Scan to auto-capture EPC from reader, or type manually. Leave blank if no tag yet.'}
          </div>
        </div>
        <div style={{ gridColumn:'1/-1' }}><Lbl>Notes</Lbl><textarea className="inp" style={{ resize:'vertical',minHeight:60 }} value={form.notes||''} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} /></div>
      </div>
      <div style={{ marginTop:14,background:'#060b10',border:'1px solid #1a2a3a',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#7090a0' }}>📋 Inventory Number auto-generated on save</div>
      <div style={{ display:'flex',gap:10,marginTop:16 }}>
        <button className="btn" style={{ flex:1,background:'none',border:'1px solid #1a2a3a',color:'#a0b4c0' }} onClick={()=>setModal(null)}>Cancel</button>
        <button className="btn" style={{ flex:2,background:'#00e5a0',color:'#060b10' }} onClick={saveAdd} disabled={saving}>{saving?'Saving…':'Register Asset'}</button>
      </div>
    </Modal>}

    {/* View/Edit Modal */}
    {modal&&modal!=='add'&&<Modal title={modal.name} sub={`${modal.type || 'No type'} · EPC: ${modal.epc || '—'}`} onClose={()=>setModal(null)} wide noClickOutside>
      <div style={{ display:'flex',gap:2,borderBottom:'1px solid #1a2a3a',marginBottom:18,alignItems:'center' }}>
        {[['details',<>Details</>],['inventory',<>Inventory</>],features.can_maintenance&&['maintenance',<span style={{display:'inline-flex',alignItems:'center',gap:6}}><Wrench size={13}/>Maintenance</span>],features.can_attachments&&['attachments',<span style={{display:'inline-flex',alignItems:'center',gap:6}}><Paperclip size={13}/>Attachments</span>],features.can_qr_label&&['qr',<span style={{display:'inline-flex',alignItems:'center',gap:6}}><QrCode size={13}/>QR Label</span>]].filter(Boolean).map(([t,lbl])=><button key={t} className="tab-btn" onClick={e=>{e.stopPropagation();setTab(t);if(t==='maintenance'&&modal?.id)loadMaintHistory(modal.id);if(t==='attachments'&&modal?.id)loadAttachments(modal.id);}} style={{ color:tab===t?'#00e5a0':'#7090a0',borderBottom:tab===t?'2px solid #00e5a0':'2px solid transparent' }}>{lbl}</button>)}
        <div style={{ flex:1 }}/>
        <button className="btn" style={{ fontSize:11,padding:'5px 14px',background:'none',border:'1px solid #1a2a3a',color:'#a0b4c0',display:'inline-flex',alignItems:'center',gap:6 }} onClick={()=>printLabel(modal)}><Printer size={13}/>Print Label</button>
      </div>
      {tab==='maintenance'&&<div>
        {/* Maintenance Status */}
        {(() => {
          const ms = getMaintenanceStatus(modal);
          return (
            <div style={{ background:ms.color+'18',border:`1px solid ${ms.color}30`,borderRadius:10,padding:'14px 16px',marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center' }}>
              <div>
                <div style={{ fontSize:11,color:'#7090a0',marginBottom:2 }}>Maintenance Status</div>
                <div style={{ fontSize:18,fontWeight:800,color:ms.color }}>{ms.label}</div>
              </div>
              <div style={{ fontSize:32 }}>{ms.label==='OK'?'✅':ms.label.startsWith('Due')?'⚠️':'🔴'}</div>
            </div>
          );
        })()}

        {/* Maintenance Fields */}
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
          <div>
            <Lbl>Last Maintenance Date</Lbl>
            <input className="inp" type="date" value={form.maintenance_date||''} disabled={!canEdit}
              onChange={e=>setForm(f=>({...f,maintenance_date:e.target.value}))}/>
          </div>
          <div>
            <Lbl>Next Maintenance Date</Lbl>
            <input className="inp" type="date" value={form.next_maintenance_date||''} disabled={!canEdit}
              onChange={e=>setForm(f=>({...f,next_maintenance_date:e.target.value}))}/>
          </div>
          <div>
            <Lbl>Maintenance Interval (days)</Lbl>
            <select className="sel" style={{ width:'100%' }} value={form.maintenance_interval||365} disabled={!canEdit}
              onChange={e=>setForm(f=>({...f,maintenance_interval:Number(e.target.value)}))}>
              {MAINT_INTERVALS.map(iv=>
                <option key={iv.days} value={iv.days}>{iv.label} ({iv.days} days)</option>
              )}
            </select>
            {/* Auto-calc next date — sits directly under the interval dropdown */}
            {canEdit && form.maintenance_date && form.maintenance_interval && (
              <button className="btn" style={{ marginTop:8,width:'100%',background:'#5b9cf618',border:'1px solid #5b9cf640',color:'#5b9cf6',fontSize:11 }}
                onClick={() => {
                  const next = new Date(form.maintenance_date);
                  next.setDate(next.getDate() + Number(form.maintenance_interval));
                  setForm(f=>({...f,next_maintenance_date:next.toISOString().slice(0,10)}));
                }}>
                🔄 Auto-calculate next date
              </button>
            )}
          </div>
          <div>
            <div style={{ display:'flex',alignItems:'center',gap:8 }}>
              <Lbl>Expiry date</Lbl>
              {(() => {
                if (!form.warranty_expiry) return null;
                const exp = new Date(form.warranty_expiry);
                const today = new Date(); today.setHours(0,0,0,0);
                const days = Math.ceil((exp - today) / 86400000);
                let label, color;
                if (days < 0)       { label = `Expired ${Math.abs(days)}d ago`; color = '#e05757'; }
                else if (days === 0){ label = 'Expires today';                  color = '#e05757'; }
                else if (days <= 30){ label = `${days}d left`;                   color = '#f5a623'; }
                else                { label = `${days}d left`;                   color = '#00e5a0'; }
                return (
                  <span style={{ fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:10,
                    marginBottom:6,background:color+'18',border:`1px solid ${color}40`,color }}>
                    {label}
                  </span>
                );
              })()}
            </div>
            <input className="inp" type="date" value={form.warranty_expiry||''} disabled={!canEdit}
              onChange={e=>setForm(f=>({...f,warranty_expiry:e.target.value}))}/>
          </div>
          <div style={{ gridColumn:'1/-1' }}>
            <Lbl>Maintained By (Person / Company)</Lbl>
            <input className="inp" type="text" value={form.maintained_by||''} disabled={!canEdit}
              onChange={e=>setForm(f=>({...f,maintained_by:e.target.value}))}
              placeholder="e.g. John Doe, ABC Service Sdn Bhd"/>
          </div>
          <div style={{ gridColumn:'1/-1' }}>
            <Lbl>Maintenance Notes</Lbl>
            <textarea className="inp" rows={3} style={{ resize:'vertical' }} value={form.maintenance_notes||''} disabled={!canEdit}
              onChange={e=>setForm(f=>({...f,maintenance_notes:e.target.value}))}
              placeholder="e.g. Replace oil filter, check brake pads..."/>
          </div>
        </div>

        {canEdit && (
          <div style={{ display:'flex',gap:10,marginTop:16 }}>
            <button className="btn" style={{ flex:1,background:'none',border:'1px solid #1a2a3a',color:'#a0b4c0' }} onClick={()=>setModal(null)}>Cancel</button>
            <button className="btn" style={{ flex:2,background:'#00e5a0',color:'#060b10' }} onClick={saveEdit} disabled={saving}>{saving?'Saving…':'💾 Save Maintenance'}</button>
          </div>
        )}

        {/* Maintenance History */}
        <div style={{ marginTop:24 }}>
          <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12 }}>
            <div style={{ fontSize:13,fontWeight:700,color:'#c8d6e0',display:'inline-flex',alignItems:'center',gap:6 }}><ClipboardList size={14}/>Maintenance History</div>
            {canEdit && <button className="btn" style={{ fontSize:12,padding:'5px 12px',background:'#5b9cf618',border:'1px solid #5b9cf640',color:'#5b9cf6' }}
              onClick={()=>setShowLogForm(s=>!s)}>
              {showLogForm ? '✕ Cancel' : '+ Log Maintenance'}
            </button>}
          </div>

          {/* Log Form */}
          {showLogForm && (
            <div style={{ background:'#0d1821',border:'1px solid #1a2a3a',borderRadius:10,padding:14,marginBottom:14 }}>
              <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10 }}>
                <div><Lbl>Date Performed</Lbl>
                  <input className="inp" type="date" value={maintLog.performed_at}
                    onChange={e=>setMaintLog(l=>({...l,performed_at:e.target.value}))}/>
                </div>
                <div><Lbl>Cost (RM)</Lbl>
                  <input className="inp" type="number" placeholder="0.00" value={maintLog.cost}
                    onChange={e=>setMaintLog(l=>({...l,cost:e.target.value}))}/>
                </div>
                <div style={{ gridColumn:'1/-1' }}><Lbl>What was done</Lbl>
                  <textarea className="inp" rows={2} style={{ resize:'vertical' }} value={maintLog.notes}
                    placeholder="e.g. Oil change, filter replaced, brake inspection..."
                    onChange={e=>setMaintLog(l=>({...l,notes:e.target.value}))}/>
                </div>
              </div>
              <button className="btn" style={{ width:'100%',background:'#00e5a0',color:'#060b10',fontSize:13 }}
                onClick={logMaintenance} disabled={saving||!maintLog.notes}>
                {saving ? 'Saving…' : '✅ Save Maintenance Log'}
              </button>
            </div>
          )}

          {/* History List */}
          {maintLoading ? <div style={{ color:'#7090a0',fontSize:12,padding:12 }}>Loading…</div> :
            maintHistory.length === 0 ? (
              <div style={{ background:'#0d1821',borderRadius:10,padding:16,textAlign:'center',color:'#7090a0',fontSize:13 }}>
                No maintenance history yet
              </div>
            ) : maintHistory.map(h => (
              <div key={h.id} style={{ background:'#0d1821',border:'1px solid #1a2a3a',borderRadius:10,padding:12,marginBottom:8 }}>
                <div style={{ display:'flex',justifyContent:'space-between',marginBottom:4 }}>
                  <span style={{ fontSize:12,fontWeight:700,color:'#00e5a0' }}>{h.performed_at}</span>
                  {h.cost && <span style={{ fontSize:11,color:'#f5a623' }}>RM {Number(h.cost).toFixed(2)}</span>}
                </div>
                <div style={{ fontSize:13,color:'#c8d6e0',marginBottom:4 }}>{h.notes}</div>
                <div style={{ fontSize:11,color:'#7090a0' }}>By {h.performed_by || '—'}</div>
              </div>
            ))
          }
        </div>
      </div>}

      {tab==='details'&&<div>
        {canEdit && (
          <div style={{ marginBottom:16 }}>
            <Lbl>{isISO ? 'Equipment Name' : 'Asset Name'}</Lbl>
            <div style={{ display:'flex',gap:10 }}>
              <input className="inp" style={{ flex:1 }} value={form.name||''}
                placeholder={isISO ? 'e.g. Chilled Water/Sea Water Exchanger' : 'e.g. Dell Laptop XPS15'}
                onChange={e=>setForm(f=>({...f,name:e.target.value}))}/>
              <button className="btn" style={{ background:'#00e5a0',color:'#060b10',whiteSpace:'nowrap' }}
                onClick={saveEdit} disabled={saving||!form.name?.trim()}>{saving?'Saving…':'Rename'}</button>
            </div>
          </div>
        )}
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:16 }}>
          {(isISO
            ? [['Platform',modal.platform],['Location (Facility)',modal.current_facility_name],['PHE Type',modal.phe_type],['Brand',modal.brand],['Model',modal.model],['Tag No',modal.tag_no],['Serial No.',modal.serial_no],['Year Built',modal.year_built],['Last Service',modal.maintenance_date],['Preventive Maintenance',modal.preventive_maintenance],['Next Service',modal.next_maintenance_date],['Status',modal.status]]
            : [['Asset ID',modal.id],['Inventory No.',modal.inventory_number],['Serial No.',modal.serial_no],['Type',modal.type],['Brand',modal.brand],['Condition',modal.condition],['Status',modal.status],['Current Facility',modal.current_facility_name],['Checked Out To',modal.checked_out_to||'—'],['Last Seen',modal.last_seen]]
          ).map(([k,v])=>(
            <div key={k} style={{ background:'#060b10',border:'1px solid #1a2a3a',borderRadius:8,padding:'10px 14px' }}>
              <div style={{ fontSize:10,color:'#7090a0',textTransform:'uppercase',letterSpacing:'1px',marginBottom:4 }}>{k}</div>
              <div style={{ fontSize:13,color:'#c8d6e0',fontFamily:['EPC Code','Inventory No.','Asset ID','Serial No.'].includes(k)?"'IBM Plex Mono',monospace":'inherit' }}>{v||'—'}</div>
            </div>
          ))}
          {/* EPC Code box — with Release button if decommissioned */}
          <div style={{ background:'#060b10',border:`1px solid ${modal.epc_released?'#00e5a040':'#1a2a3a'}`,borderRadius:8,padding:'10px 14px',gridColumn:'1/-1' }}>
            <div style={{ fontSize:10,color:'#7090a0',textTransform:'uppercase',letterSpacing:'1px',marginBottom:6 }}>EPC Code</div>
            <div style={{ display:'flex',alignItems:'center',gap:10,flexWrap:'wrap' }}>
              <div style={{ fontFamily:"'IBM Plex Mono',monospace",fontSize:13,color:'#c8d6e0',flex:1 }}>{modal.epc||'—'}</div>
              {epcRecycleEnabled && modal.status==='Decommissioned' && modal.epc && (
                modal.epc_released ? (
                  <div style={{ display:'flex',alignItems:'center',gap:6,background:'#00e5a015',border:'1px solid #00e5a030',borderRadius:6,padding:'4px 10px',fontSize:11,color:'#00e5a0' }}>
                    ♻ Released — available for reuse
                  </div>
                ) : (
                  <button className="btn"
                    style={{ background:'#00e5a018',border:'1px solid #00e5a040',color:'#00e5a0',fontSize:11,padding:'5px 12px',whiteSpace:'nowrap' }}
                    onClick={()=>releaseEpc(modal).then(()=>setModal(m=>({...m,epc_released:true})))}>
                    <RefreshCw size={13} style={{marginRight:6,verticalAlign:'-2px'}}/>Release EPC
                  </button>
                )
              )}
            </div>
          </div>
        </div>
        {perms.canDelete && modal?.status !== 'Decommissioned' && (
        <div style={{ marginBottom:16 }}>
          <button className="btn"
            style={{ background:'#e0575718',border:'1px solid #e0575740',color:'#e05757',fontSize:12,padding:'8px 16px' }}
            onClick={()=>{ setDecommReason(''); setDecommModal(modal); }}>
            <Ban size={13} style={{marginRight:6,verticalAlign:'-2px'}}/>Decommission Asset
          </button>
        </div>
      )}
      {modal?.status === 'Decommissioned' && (
        <div style={{ background:'#e0575710',border:'1px solid #e0575730',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#e05757',marginBottom:16 }}>
          <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start' }}>
            <div>
              🚫 Decommissioned {modal.decommissioned_at ? new Date(modal.decommissioned_at).toLocaleDateString() : ''}
              {modal.decommission_reason && <div style={{ marginTop:4,color:'#a0b4c0' }}>Reason: {modal.decommission_reason}</div>}
            </div>
            {isAdmin && (
              <button className="btn" style={{ background:'#e0575725',border:'1px solid #e0575760',color:'#e05757',fontSize:11,padding:'4px 12px',flexShrink:0,marginLeft:12 }}
                onClick={() => deleteAsset(modal)}>
                🗑 Delete Permanently
              </button>
            )}
          </div>
        </div>
      )}
      {isAdmin && (
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14 }}>
          <div><Lbl>Brand</Lbl><input className="inp" value={form.brand||''} placeholder="e.g. Lenovo" onChange={e=>setForm(f=>({...f,brand:e.target.value}))}/></div>
          <div><Lbl>Model</Lbl><input className="inp" value={form.model||''} placeholder="e.g. ThinkPad X1" onChange={e=>setForm(f=>({...f,model:e.target.value}))}/></div>
          <div style={{ gridColumn:'1/-1' }}><Lbl>Serial Number</Lbl><input className="inp" value={form.serial_no||''} placeholder="e.g. SN-2024-XXXXX" onChange={e=>setForm(f=>({...f,serial_no:e.target.value}))}/></div>
        </div>
      )}
      {isISO && isAdmin && (
        <div style={{ marginBottom:14,padding:14,background:'#0d1821',border:'1px solid #1a2a3a',borderRadius:10 }}>
          <div style={{ fontSize:11,fontWeight:700,color:'#5b9cf6',textTransform:'uppercase',letterSpacing:'1px',marginBottom:10 }}>ISO 55000 Fields</div>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10 }}>
            <div><Lbl>Platform</Lbl><input className="inp" value={form.platform||''} placeholder="e.g. CKP" onChange={e=>setForm(f=>({...f,platform:e.target.value}))}/></div>
            <div><Lbl>PHE Type</Lbl><input className="inp" value={form.phe_type||''} placeholder="e.g. Gasketed" onChange={e=>setForm(f=>({...f,phe_type:e.target.value}))}/></div>
            <div><Lbl>Tag No</Lbl><input className="inp" value={form.tag_no||''} placeholder="e.g. CKP-E-4438A" onChange={e=>setForm(f=>({...f,tag_no:e.target.value}))}/></div>
            <div><Lbl>Year Built</Lbl><input className="inp" type="number" min="1900" max="2100" value={form.year_built||''} placeholder="e.g. 2001" onChange={e=>setForm(f=>({...f,year_built:e.target.value}))}/></div>
            <div><Lbl>Preventive Maintenance</Lbl><input className="inp" value={form.preventive_maintenance||''} placeholder="e.g. 5 Years after last service" onChange={e=>setForm(f=>({...f,preventive_maintenance:e.target.value}))}/></div>
          </div>
        </div>
      )}
      <div><Lbl>Notes</Lbl><textarea className="inp" style={{ resize:'vertical',minHeight:60 }} value={form.notes||''} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} disabled={!canEdit}/>{canEdit&&<div style={{ display:'flex',justifyContent:'flex-end',marginTop:12 }}><button className="btn" style={{ background:'#00e5a0',color:'#060b10' }} onClick={saveEdit} disabled={saving}>{saving?'Saving…':'Save Notes'}</button></div>}</div>
      </div>}
      {tab==='inventory'&&<div>
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
          <div><Lbl>Asset Status</Lbl><select className="sel" style={{ width:'100%' }} value={form.status||''} onChange={e=>setForm(f=>({...f,status:e.target.value}))} disabled={!canEdit}>{['Available','In Use','Under Maintenance','In Transit'].map(s=><option key={s}>{s}</option>)}</select></div>
          {!isISO && <>
            <div><Lbl>Condition</Lbl><select className="sel" style={{ width:'100%' }} value={form.condition||''} onChange={e=>setForm(f=>({...f,condition:e.target.value}))} disabled={!canEdit}>{CONDITIONS.map(c=><option key={c.id||c} value={c.name||c}>{c.name||c}</option>)}</select></div>
            <div><Lbl>Value (MYR)</Lbl><input className="inp" type="number" value={form.value||''} onChange={e=>setForm(f=>({...f,value:e.target.value}))} disabled={!perms.canDelete}/></div>
            <div><Lbl>Quantity</Lbl><input className="inp" type="number" min="0" step="1" value={form.quantity??''} placeholder="e.g. 12" onChange={e=>setForm(f=>({...f,quantity:e.target.value}))} disabled={!canEdit}/></div>
            <div><Lbl>Unit</Lbl>
              <select className="sel" style={{ width:'100%' }} value={form.quantity_unit||'pcs'} onChange={e=>setForm(f=>({...f,quantity_unit:e.target.value}))} disabled={!canEdit}>
                {['pcs','carton','box','pack','set','unit','roll','pallet','kg','litre','metre'].map(u=><option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </>}
        </div>
        <div style={{ display:'flex',gap:10,marginTop:18 }}><button className="btn" style={{ flex:1,background:'none',border:'1px solid #1a2a3a',color:'#a0b4c0' }} onClick={()=>setModal(null)}>Cancel</button>{canEdit&&<button className="btn" style={{ flex:2,background:'#00e5a0',color:'#060b10' }} onClick={saveEdit} disabled={saving}>{saving?'Saving…':'💾 Update Asset'}</button>}</div>
      </div>}
      {tab==='attachments'&&<div>
        {/* Upload area */}
        <div style={{ background:'#0d1821',border:'2px dashed #1a2a3a',borderRadius:12,padding:24,textAlign:'center',marginBottom:16,
          cursor:'pointer',transition:'border-color .2s' }}
          onDragOver={e=>{e.preventDefault();e.currentTarget.style.borderColor='#00e5a0';}}
          onDragLeave={e=>{e.currentTarget.style.borderColor='#1a2a3a';}}
          onDrop={e=>{e.preventDefault();e.currentTarget.style.borderColor='#1a2a3a';const f=e.dataTransfer.files[0];if(f)uploadAttachment(f,modal?.id);}}
          onClick={()=>document.getElementById('att-file-input').click()}>
          <input id="att-file-input" type="file" style={{ display:'none' }}
            onChange={e=>{const f=e.target.files[0];if(f)uploadAttachment(f,modal?.id);e.target.value='';}}/>
          {attUploading
            ? <><div style={{ fontSize:24,marginBottom:8 }}>⏳</div><div style={{ color:'#00e5a0',fontSize:13 }}>Uploading…</div></>
            : <><div style={{ fontSize:32,marginBottom:8 }}>📎</div>
               <div style={{ color:'#c8d6e0',fontSize:13,fontWeight:600 }}>Click or drag & drop to upload</div>
               <div style={{ color:'#7090a0',fontSize:11,marginTop:4 }}>PDF, Word, Excel, Images, ZIP — max 10MB</div></>
          }
        </div>

        {/* Category selector */}
        <div style={{ display:'flex',gap:8,marginBottom:16,flexWrap:'wrap' }}>
          <span style={{ fontSize:11,color:'#7090a0',alignSelf:'center' }}>Category:</span>
          {ATT_CATS.map(c=>(
            <button key={c} onClick={()=>setAttCategory(c)}
              style={{ fontSize:11,padding:'4px 10px',borderRadius:6,border:'1px solid',cursor:'pointer',
                background:attCategory===c?'#00e5a018':'none',
                borderColor:attCategory===c?'#00e5a0':'#1a2a3a',
                color:attCategory===c?'#00e5a0':'#7090a0' }}>
              {c}
            </button>
          ))}
        </div>

        {/* File list */}
        {attLoading
          ? <div style={{ color:'#7090a0',fontSize:13,textAlign:'center',padding:20 }}>Loading…</div>
          : attachments.length === 0
            ? <div style={{ background:'#0d1821',borderRadius:10,padding:24,textAlign:'center',color:'#7090a0',fontSize:13 }}>
                No attachments yet — upload your first file above
              </div>
            : attachments.map(att => (
                <div key={att.id} style={{ background:'#0d1821',border:'1px solid #1a2a3a',borderRadius:10,
                  padding:'12px 14px',marginBottom:8,display:'flex',alignItems:'center',gap:12 }}>
                  <div style={{ fontSize:28,flexShrink:0 }}>{fileIcon(att.file_type)}</div>
                  <div style={{ flex:1,minWidth:0 }}>
                    <div style={{ fontSize:13,fontWeight:600,color:'#e8edf2',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>
                      {att.file_name}
                    </div>
                    <div style={{ fontSize:11,color:'#7090a0',marginTop:2 }}>
                      <span style={{ background:'#1a2a3a',borderRadius:4,padding:'1px 6px',marginRight:6 }}>{att.category}</span>
                      {fmtSize(att.file_size)} · {att.uploaded_by} · {new Date(att.created_at).toLocaleDateString('en-MY')}
                    </div>
                  </div>
                  <div style={{ display:'flex',gap:6,flexShrink:0 }}>
                    <button onClick={()=>downloadAttachment(att)}
                      style={{ background:'#5b9cf618',border:'1px solid #5b9cf640',color:'#5b9cf6',borderRadius:6,
                        padding:'5px 10px',cursor:'pointer',fontSize:12,fontFamily:'inherit' }}>
                      ↓
                    </button>
                    {canEdit && <button onClick={()=>deleteAttachment(att)}
                      style={{ background:'#e0575718',border:'1px solid #e0575730',color:'#e05757',borderRadius:6,
                        padding:'5px 10px',cursor:'pointer',fontSize:12,fontFamily:'inherit' }}>
                      🗑
                    </button>}
                  </div>
                </div>
              ))
        }
      </div>}

      {tab==='qr'&&<div style={{ display:'flex',gap:32,alignItems:'flex-start',flexWrap:'wrap' }}>
        <div style={{ flex:'0 0 auto',display:'flex',flexDirection:'column',alignItems:'center',gap:12 }}>
          <div style={{ background:'#fff',padding:12,borderRadius:12,display:'inline-block' }}>
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(JSON.stringify({epc:modal.epc||'',name:modal.name,inventory:modal.inventoryNumber||'',status:modal.status||'',facility:modal.currentFacilityName||'',company:org?.name||''}))}&format=png&margin=6`}
              alt="QR Code"
              style={{ width:200,height:200,display:'block' }}
            />
          </div>
          <div style={{ fontSize:10,color:'#7090a0',fontFamily:"'IBM Plex Mono',monospace",textAlign:'center',maxWidth:220,wordBreak:'break-all' }}>
            {modal.epc||modal.inventoryNumber}
          </div>
          <button className="btn" style={{ background:'#00e5a0',color:'#060b10',width:'100%' }} onClick={()=>printLabel(modal)}>🖨 Print Label</button>
          <button className="btn" style={{ background:'none',border:'1px solid #1a2a3a',color:'#a0b4c0',width:'100%',fontSize:12 }}
            onClick={()=>{ const a=document.createElement('a'); a.href=`https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(JSON.stringify({epc:modal.epc||'',name:modal.name,inventory:modal.inventoryNumber||'',status:modal.status||'',facility:modal.currentFacilityName||'',company:org?.name||''}))}&format=png`; a.download=`QR-${modal.inventoryNumber||modal.id}.png`; a.click(); }}>
            ⬇ Download QR PNG
          </button>
        </div>
        <div style={{ flex:1,minWidth:220 }}>
          <div style={{ marginBottom:16 }}>
            <Lbl>QR contains</Lbl>
            <div style={{ background:'#060b10',border:'1px solid #1a2a3a',borderRadius:8,padding:'10px 14px',display:'grid',gap:8 }}>
              {[['Company',org?.name],['Asset Name',modal.name],['Status',modal.status],['Facility',modal.currentFacilityName],['Inventory No.',modal.inventoryNumber],['EPC Tag',modal.epc],['Serial No.',modal.serial_no]].map(([k,v])=>(
                <div key={k} style={{ display:'flex',gap:10,alignItems:'baseline' }}>
                  <div style={{ fontSize:10,color:'#7090a0',textTransform:'uppercase',letterSpacing:'1px',width:90,flexShrink:0 }}>{k}</div>
                  <div style={{ fontSize:12,color: k==='Status'?{Available:'#00e5a0','In Use':'#5b9cf6','Under Maintenance':'#f5a623','In Transit':'#b89de8'}[v]||'#c8d6e0':'#c8d6e0',
                    fontFamily:['EPC Tag','Inventory No.','Serial No.'].includes(k)?"'IBM Plex Mono',monospace":'inherit',fontWeight:k==='Status'?700:400 }}>{v||'—'}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ background:'#060b10',border:'1px solid #1a2a3a',borderRadius:8,padding:'10px 14px',fontSize:11,color:'#7090a0',lineHeight:1.7 }}>
            💡 Scan this QR with any phone camera or scanner to instantly identify this asset — includes company, status and facility.
          </div>
        </div>
      </div>}
    </Modal>}
    {decommModal && (
      <Modal title="Decommission Asset" sub={decommModal.name} onClose={()=>setDecommModal(null)} noClickOutside>
        <div style={{ background:'#e0575710',border:'1px solid #e0575730',borderRadius:8,padding:'12px 16px',marginBottom:18 }}>
          <div style={{ fontWeight:700,color:'#e05757',fontSize:13,marginBottom:4 }}>⚠ This cannot be easily undone</div>
          <div style={{ fontSize:12,color:'#a0b4c0' }}>The asset will be marked Decommissioned and removed from active tracking.</div>
        </div>
        {decommHide !== 'never' && (
          <div style={{ background:'#f5a62310',border:'1px solid #f5a62330',borderRadius:8,padding:'10px 14px',fontSize:11,color:'#f5a623',marginBottom:16 }}>
            ℹ Will be auto-hidden after {decommHide} month{decommHide!=='1'?'s':''} per your settings.
          </div>
        )}
        <div style={{ marginBottom:14 }}>
          <Lbl>Reason {decommReqReason ? '*' : '(optional)'}</Lbl>
          <textarea className="inp" style={{ resize:'vertical',minHeight:80 }}
            value={decommReason}
            placeholder="e.g. End of life, damaged beyond repair…"
            onChange={e=>setDecommReason(e.target.value)}/>
        </div>
        <div style={{ display:'flex',gap:10 }}>
          <button className="btn" style={{ flex:1,background:'none',border:'1px solid #1a2a3a',color:'#a0b4c0' }} onClick={()=>setDecommModal(null)}>Cancel</button>
          <button className="btn" style={{ flex:2,background:'#e05757',color:'#fff' }}
            onClick={()=>decommissionAsset(decommModal)}
            disabled={decommReqReason && !decommReason.trim()}>
            🚫 Confirm Decommission
          </button>
        </div>
      </Modal>
    )}

  </div>;
}
